import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import type { RecommendPlaybooksWidgetData } from '@playbooks/action-cards-utils'

import { useTranslation } from '../../contexts/localization/Localization'
import { type WidgetProps } from './widgetTypes'
import { CustomButton } from '../../components/ui/CustomButton'
import { WarningCircleIcon } from '../../components/ui'
import { useAppContext } from '../../contexts/AppContext'

interface RecommendPlaybooksWidgetProps extends WidgetProps {
  widget: RecommendPlaybooksWidgetData
}

/**
 * Widget that triggers playbook recommendations
 */
const RecommendPlaybooksWidget: FunctionComponent<RecommendPlaybooksWidgetProps> = ({ widget }) => {
  const { config, availableTags, data } = useAppContext()
  const { text: t } = useTranslation()
  const { settings } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [playbooks, setPlaybooks] = useState<any[]>()

  async function handleButtonClick() {
    setIsLoading(true)
    const tags: string[] = []
    data.conditionalItems?.map(tagId => {
      tags.push(availableTags[tagId])
    })

    try {
      const playbooks = await config?.recommendPlaybooks?.(tags)
      setPlaybooks(playbooks || [])
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  function renderPlaybook(playbook: any) {
    return (
      <a
        key={playbook.id}
        className="no-underline border border-solid border-gray-300 dark:border-gray-600 rounded-full text-inherit p-2 flex gap-2 items-center
          hover:bg-gray-100 dark:hover:bg-gray-800 dark:bg-gray-900"
        href={`${settings?.tenantUrl}/actionroute.ashx?object=resource&action=add&type=fom_project&input=type%3Dfom_project%26attr_fom_meetingPlaybook%3D${playbook.id}`}
        target="_blank"
      >
        <img className="w-6 h-6 rounded-full" src={playbook.image} alt={playbook.name} />
        <span className="whitespace-nowrap overflow-hidden text-ellipsis dark:text-gray-100">{playbook.name}</span>
      </a>
    )
  }

  return (
    <div>
      <div className="flex">
        <CustomButton
          size="lg"
          isLoading={isLoading}
          onClick={async () => {
            if (!isLoading) await handleButtonClick()
          }}
        >
          <span className="flex items-center gap-2">
            <Sparkles />
            {t('recommendPlaybooks')}
          </span>
        </CustomButton>
      </div>
      {playbooks && playbooks.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">{playbooks.map(playbook => renderPlaybook(playbook))}</div>
      )}
      {playbooks && !playbooks.length && (
        <div className="mt-2 flex items-center justify-center gap-2 text-ellipsis dark:text-gray-100">
          <WarningCircleIcon />
          {t('noPlaybooksFound')}
        </div>
      )}
    </div>
  )
}

export default RecommendPlaybooksWidget
